import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { DeletionImpact } from '@/generated/api/models/deletionImpact';
import type { DeletionOption } from '@/generated/api/models/deletionOption';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { DialogForm } from './DialogForm';
import { BidiText } from './BidiText';
import { EntityChip } from './EntityChip';
import { LtrToken } from './LtrToken';

/** An option carrying `context.control` is rendered by the caller (via
 * `extraContent`), not as a boolean switch — e.g. the user hard-delete
 * reassignment picker. See deletion-and-cascade-design §5.3. */
function isSwitchOption(option: DeletionOption): boolean {
  return !(option.context && typeof option.context.control === 'string');
}

export interface DeleteImpactDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (options: Record<string, boolean>) => void | Promise<void>;
  /** The deletion-impact preview for the entity, fetched when the dialog opens. */
  impact: DeletionImpact | undefined;
  isLoadingImpact: boolean;
  /** The impact fetch failed — fail closed: disable confirm and show an error. */
  isImpactError?: boolean;
  isDeleting: boolean;
  /** Human-readable name of the entity being deleted (e.g. the product name). */
  entityLabel: string;
  /** i18n key prefix for entity-specific cascade/reference/option labels, e.g. "products:delete". */
  keyPrefix: string;
  /**
   * Type-to-confirm phrase for high-impact deletes (hard deletes, bulk). When
   * set, an input is shown and the confirm button stays disabled until the user
   * types this phrase exactly (deletion-and-cascade-design §5.3).
   */
  confirmPhrase?: string;
  /**
   * Caller-owned content rendered after the options section — e.g. the user
   * hard-delete reassignment picker or a sole-user tenant-deletion warning.
   * Options tagged with `context.control` are NOT rendered as switches; the
   * caller renders their control here instead.
   */
  extraContent?: ReactNode;
  /** Caller-driven submit gate (e.g. a required reassignment target unselected). */
  extraDisabled?: boolean;
  /** Override the "…via a snapshot and are unaffected" help under kept references. */
  referencesKeptHelp?: string;
  /** Override the default "Delete <entity>?" title. */
  title?: ReactNode;
  /** Override the default "Delete" submit label. */
  submitLabel?: string;
}

/**
 * Generic "delete → impact preview → confirm with options" dialog
 * (docs/platform/deletion-and-cascade-design.md §5.3). Renders the shared
 * {@link DeletionImpact} payload: refuses the delete while blockers exist, lists
 * cascades and surviving references, and lets the user toggle any offered options.
 * Entity-specific copy is resolved from `keyPrefix`; this component holds no
 * product/entity-specific strings.
 */
export function DeleteImpactDialog({
  open,
  onClose,
  onConfirm,
  impact,
  isLoadingImpact,
  isImpactError = false,
  isDeleting,
  entityLabel,
  keyPrefix,
  confirmPhrase,
  extraContent,
  extraDisabled = false,
  referencesKeptHelp,
  title,
  submitLabel,
}: DeleteImpactDialogProps) {
  const { t } = useTranslation('common');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});
  const [confirmText, setConfirmText] = useState('');

  const blockers = impact?.blockers ?? [];
  const cascades = impact?.cascades ?? [];
  const referencesKept = impact?.references_kept ?? [];
  const options = impact?.options ?? [];
  const switchOptions = options.filter(isSwitchOption);

  // Seed each option with its safe default whenever a fresh impact loads.
  useEffect(() => {
    setSelectedOptions(Object.fromEntries(options.map((o) => [o.key, o.default])));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed only when the impact payload changes
  }, [impact]);

  // Reset the type-to-confirm input each time the dialog (re)opens.
  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const hasBlockers = blockers.length > 0;
  const isEmpty =
    !isLoadingImpact &&
    !isImpactError &&
    !!impact &&
    blockers.length === 0 &&
    cascades.length === 0 &&
    referencesKept.length === 0 &&
    options.length === 0;
  const confirmPhraseUnmet = !!confirmPhrase && confirmText !== confirmPhrase;
  // Fail closed: never allow the destructive confirm until the impact preview has
  // successfully loaded (so a fetch error can't present an apparently-clear delete).
  const confirmDisabled =
    hasBlockers ||
    isLoadingImpact ||
    isImpactError ||
    !impact ||
    confirmPhraseUnmet ||
    extraDisabled;

  const handleSubmit = () => {
    void onConfirm(selectedOptions);
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={
        title ?? (
          <Trans
            t={t}
            i18nKey="common:deleteImpact.title"
            values={{ entity: entityLabel }}
            components={{ n: <BidiText /> }}
          />
        )
      }
      submitLabel={submitLabel ?? t('common:app.delete')}
      isLoading={isDeleting}
      isDisabled={confirmDisabled}
      destructive
      maxWidth="md"
    >
      <div className="flex flex-col gap-4">
        {isLoadingImpact && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Impact fetch failed — fail closed: no impact shown, confirm disabled. */}
        {isImpactError && !isLoadingImpact && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t('common:deleteImpact.loadError')}</AlertDescription>
          </Alert>
        )}

        {/* 1. Blockers — deletion refused until cleared. Each names the records
            standing in the way, as chips the user can open in a new tab: the
            dialog is a to-do list, and following one must not lose the dialog. */}
        {hasBlockers && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <ul className="space-y-3">
                {blockers.map((b) => (
                  <li key={b.code} className="space-y-1">
                    <span>{t(b.label_key, { count: b.count })}</span>
                    {b.samples.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {b.samples.map((sample) => (
                          <EntityChip
                            key={sample.entity_ref}
                            entity={sample}
                            size="sm"
                            openInNewTab
                          />
                        ))}
                      </div>
                    )}
                    {/* Records the caller's own scope hides. Said out loud, so an
                        unclearable refusal reads as "ask a colleague" rather than
                        as a bug — never folded silently into the count above. */}
                    {b.hidden_count > 0 && (
                      <p className="text-xs opacity-90">
                        {t('common:deleteImpact.hiddenBlockers', { count: b.hidden_count })}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* 2. Cascades — data that will be removed. */}
        {cascades.length > 0 && (
          <div className="space-y-1.5">
            <h4 className="text-sm font-medium">{t('common:deleteImpact.cascadesTitle')}</h4>
            <ul className="space-y-1">
              {cascades.map((c) => (
                <li key={c.key} className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                  <span>{t(`${keyPrefix}.cascades.${c.key}`)}</span>
                  <span className="shrink-0 tabular-nums">{c.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 3. Options — user decisions at delete time (boolean switches only;
            control-tagged options are rendered by the caller via extraContent). */}
        {switchOptions.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">{t('common:deleteImpact.optionsTitle')}</h4>
            {switchOptions.map((opt) => (
              <div key={opt.key} className="rounded-md border border-border p-3">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor={`delete-option-${opt.key}`} className="cursor-pointer">
                    {t(`${keyPrefix}.options.${opt.key}.label`)}
                  </Label>
                  <Switch
                    id={`delete-option-${opt.key}`}
                    checked={selectedOptions[opt.key] ?? opt.default}
                    onCheckedChange={(checked) =>
                      setSelectedOptions((prev) => ({ ...prev, [opt.key]: checked }))
                    }
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <Trans
                    t={t}
                    i18nKey={`${keyPrefix}.options.${opt.key}.description`}
                    values={opt.context}
                    components={{ n: <LtrToken /> }}
                  />
                </p>
              </div>
            ))}
          </div>
        )}

        {/* 4. References kept — survive via snapshot / reassignment. */}
        {referencesKept.length > 0 && (
          <div className="space-y-1.5">
            <h4 className="text-sm font-medium">{t('common:deleteImpact.referencesKeptTitle')}</h4>
            <ul className="space-y-1">
              {referencesKept.map((r) => (
                <li key={r.key} className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                  {/* A reference contributed by ANOTHER module carries its own
                      fully-qualified key — the host dialog has no copy of that
                      module's vocabulary. Same-module references keep the
                      keyPrefix form they have always used. */}
                  <span>{t(r.label_key ?? `${keyPrefix}.references.${r.key}`)}</span>
                  <span className="shrink-0 tabular-nums">{r.count}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {referencesKeptHelp ?? t('common:deleteImpact.referencesKeptHelp')}
            </p>
          </div>
        )}

        {isEmpty && (
          <p className="text-sm text-muted-foreground">{t('common:deleteImpact.noImpact')}</p>
        )}

        {/* Caller slot — control-tagged options (e.g. a reassignment picker) and
            entity-specific notices render here, after all impact info. */}
        {extraContent}

        {/* Type-to-confirm — gates the destructive submit for high-impact deletes. */}
        {confirmPhrase && !isLoadingImpact && !isImpactError && !!impact && !hasBlockers && (
          <div className="space-y-2">
            <Label htmlFor="delete-impact-confirm">
              <Trans
                t={t}
                i18nKey="common:deleteImpact.typeToConfirm"
                values={{ phrase: confirmPhrase }}
                components={{ n: <LtrToken /> }}
              />
            </Label>
            <Input
              id="delete-impact-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmPhrase}
              autoComplete="off"
              className={confirmText && confirmPhraseUnmet ? 'border-destructive' : ''}
            />
          </div>
        )}
      </div>
    </DialogForm>
  );
}
